// Gráficos do dashboard financeiro (M11 P3).
// Consome /community/finance/charges/dashboard-stats?team_id&months.
// Layout: 3 KPIs de período + 2 gráficos lado a lado (linha mensal + pizza método)
// + tabela top pagadores.

import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Space, Statistic, Segmented, Empty, Spin, Table, Tag } from 'antd';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  PieChart, Pie, Cell,
} from 'recharts';
import api from '../../../services/api';

const { Text, Title } = Typography;
const PRIMARY = '#128C7E';
const ACCENT = '#faad14';
const NET = '#722ed1';

const METHOD_COLORS = {
  pix: '#128C7E',
  credit_card: '#1677ff',
  debit_card: '#52c41a',
  boleto: '#faad14',
  outro: '#8c8c8c',
};
const METHOD_LABELS = {
  pix: 'Pix',
  credit_card: 'Cartão crédito',
  debit_card: 'Cartão débito',
  boleto: 'Boleto',
  outro: 'Outro',
};

const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtBRLShort = (v) => {
  const n = Number(v || 0);
  if (n >= 1000) return `R$ ${(n / 1000).toFixed(1).replace('.', ',')}k`;
  return `R$ ${n.toFixed(0)}`;
};

export default function DashboardCharts({ communityTeamId }) {
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!communityTeamId) return;
    setLoading(true);
    try {
      const r = await api.get('/community/finance/charges/dashboard-stats', {
        params: { team_id: communityTeamId, months },
      });
      setData(r.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [communityTeamId, months]);
  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const monthly = data?.monthly || [];
  const byMethod = data?.by_method || [];
  const topPayers = data?.top_payers || [];

  const hasData = monthly.some(m => Number(m.emitted_count) > 0 || Number(m.paid_count) > 0);

  return (
    <Card
      size="small"
      title={
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text strong>Visão geral</Text>
          <Segmented
            size="small"
            value={months}
            onChange={setMonths}
            options={[
              { label: '3 meses', value: 3 },
              { label: '6 meses', value: 6 },
              { label: '12 meses', value: 12 },
            ]}
          />
        </Space>
      }
      styles={{ header: { padding: '10px 12px' }, body: { padding: 12 } }}
      style={{ marginBottom: 16 }}
    >
      {loading && !data ? (
        <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
      ) : !hasData ? (
        <Empty description="Sem cobranças no período" style={{ padding: 24 }} />
      ) : (
        <>
          {/* KPIs do período */}
          <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 10 } }}>
                <Statistic
                  title={<Text style={{ fontSize: 11 }}>Emitido</Text>}
                  value={totals.emitted_amount}
                  precision={2}
                  prefix="R$"
                  valueStyle={{ fontSize: 18 }}
                />
                <Text type="secondary" style={{ fontSize: 10 }}>{totals.emitted} cobr.</Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 10 } }}>
                <Statistic
                  title={<Text style={{ fontSize: 11 }}>Pago</Text>}
                  value={totals.paid_amount}
                  precision={2}
                  prefix="R$"
                  valueStyle={{ fontSize: 18, color: PRIMARY }}
                />
                <Text type="secondary" style={{ fontSize: 10 }}>{totals.paid} pagas</Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 10 } }}>
                <Statistic
                  title={<Text style={{ fontSize: 11 }}>Conversão</Text>}
                  value={totals.conversion_rate_pct}
                  suffix="%"
                  precision={1}
                  valueStyle={{ fontSize: 18, color: totals.conversion_rate_pct >= 70 ? PRIMARY : ACCENT }}
                />
                <Text type="secondary" style={{ fontSize: 10 }}>pagas / emitidas</Text>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 10 } }}>
                <Statistic
                  title={<Text style={{ fontSize: 11 }}>Ticket médio</Text>}
                  value={totals.avg_ticket_brl}
                  precision={2}
                  prefix="R$"
                  valueStyle={{ fontSize: 18 }}
                />
                <Text type="secondary" style={{ fontSize: 10 }}>por cobrança paga</Text>
              </Card>
            </Col>
          </Row>

          {/* Gráficos */}
          <Row gutter={[12, 12]}>
            {/* Linha mensal emitido vs pago */}
            <Col xs={24} lg={16}>
              <Card size="small" title="Emitido vs Pago (mensal)" styles={{ header: { padding: '8px 12px' }, body: { padding: 8 } }}>
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <LineChart data={monthly} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtBRLShort} />
                      <Tooltip
                        // Recharts passa (value, name, item) — o `name` já é o
                        // nome da Line, então usa ele direto em vez de fazer
                        // match manual por dataKey (que caía sempre no else).
                        formatter={(v, name) => [fmtBRL(v), name]}
                        labelStyle={{ color: '#333' }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="emitted_amount" name="Emitido"
                        stroke={ACCENT} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="paid_amount" name="Pago"
                        stroke={PRIMARY} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="net_amount" name="Líquido (após taxa MP)"
                        stroke={NET} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>

            {/* Pizza por método */}
            <Col xs={24} lg={8}>
              <Card size="small" title="Por método" styles={{ header: { padding: '8px 12px' }, body: { padding: 8 } }}>
                {byMethod.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sem pagamentos" style={{ padding: 16 }} />
                ) : (
                  <div style={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={byMethod}
                          dataKey="amount"
                          nameKey="method"
                          cx="50%" cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={2}
                          label={(entry) => METHOD_LABELS[entry.method] || entry.method}
                          labelLine={false}
                          style={{ fontSize: 11 }}
                        >
                          {byMethod.map((entry, i) => (
                            <Cell key={i} fill={METHOD_COLORS[entry.method] || METHOD_COLORS.outro} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, _, e) => [fmtBRL(v), METHOD_LABELS[e.payload.method] || e.payload.method]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            </Col>

            {/* Top pagadores */}
            <Col xs={24}>
              <Card size="small" title="Top pagadores do período" styles={{ header: { padding: '8px 12px' }, body: { padding: 8 } }}>
                {topPayers.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sem pagamentos ainda" style={{ padding: 16 }} />
                ) : (
                  <Table
                    rowKey={(_, i) => i}
                    size="small"
                    pagination={false}
                    dataSource={topPayers}
                    columns={[
                      { title: '#', width: 40, render: (_, __, i) => (
                        <Tag color={i === 0 ? 'gold' : i === 1 ? 'blue' : i === 2 ? 'green' : 'default'}
                          style={{ fontSize: 11, minWidth: 24, textAlign: 'center' }}>{i + 1}</Tag>
                      ) },
                      { title: 'Nome', dataIndex: 'name', render: v => <Text strong style={{ fontSize: 12 }}>{v}</Text> },
                      { title: 'Pagas', dataIndex: 'paid_count', width: 80, align: 'right',
                        render: v => <Text style={{ fontSize: 12 }}>{v}</Text> },
                      { title: 'Total pago', dataIndex: 'paid_amount', width: 130, align: 'right',
                        render: v => <Text strong style={{ fontSize: 12, color: PRIMARY }}>{fmtBRL(v)}</Text> },
                    ]}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Card>
  );
}
